import { IconButton, Slider, SliderValue } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import FileSaver from 'file-saver';
import { useCallback, useEffect, useRef, useState } from 'react';

import styles from './audioPlayer.module.scss';

interface Props {
  className?: string;
  src?: string;
  filename?: string;
  onDelete?: VoidFunction;
  disabled?: boolean;
  isDownloadable?: boolean;
}

const getFormattedDuration = (duration: number) => {
  const hours = Math.floor(duration / 60 / 60);
  const minutes = Math.floor(duration / 60) % 60;
  const seconds = Math.floor(duration) % 60;

  const hourDuration = `${hours}`.padStart(2, '0');
  const minuteDuration = `${minutes}`.padStart(2, '0');
  const secondDuration = `${seconds}`.padStart(2, '0');

  return `${
    hours ? `${hourDuration}:` : ''
  }${minuteDuration}:${secondDuration}`;
};

export function AudioPlayer({
  className,
  onDelete,
  src,
  filename,
  disabled,
  isDownloadable,
}: Props): JSX.Element {
  const playerRef = useRef<HTMLAudioElement>(null);
  const [{ elapsed, duration }, setTime] = useState({
    elapsed: 0,
    duration: 0,
  });
  const [isPlaying, setIsPlaying] = useState(false);
  const onPlayToggle = useCallback(() => {
    const { current: player } = playerRef;

    if (!player || player.error) {
      return;
    }

    if (isPlaying) {
      player.pause();
      setIsPlaying(false);

      return;
    }

    player.play();
    setIsPlaying(true);
  }, [playerRef, isPlaying, setIsPlaying]);
  const onDurationChange = () => {
    const { current: player } = playerRef;

    if (!player) {
      return;
    }

    const isLengthLoaded = player.duration !== Infinity;

    setTime({
      elapsed: isLengthLoaded ? player.currentTime : 0,
      duration: isLengthLoaded ? player.duration : 0,
    });
  };
  const onAudioEnd = () => setIsPlaying(false);
  const onSliderMouseDown = () => {
    /*
     * When we start dragging we want to pause the audio playback,
     * otherwise we will be getting some insane alien rave sounds.
     */
    const { current: player } = playerRef;

    if (!player) {
      return;
    }

    const handleMouseUp = () => {
      window.removeEventListener('mouseup', handleMouseUp);

      setIsPlaying(true);
      player.play();
    };

    setIsPlaying(false);
    player.pause();

    window.addEventListener('mouseup', handleMouseUp);
  };
  const onSeek = (value: SliderValue) => {
    const { current: player } = playerRef;
    const elapsed = Array.isArray(value) ? value[1] : value;

    if (!player) {
      return;
    }

    player.currentTime = elapsed;
  };
  const onSave = async () => {
    if (!src) {
      return;
    }

    try {
      const response = await fetch(src, {
        method: 'GET',
        mode: 'cors',
        cache: 'no-cache',
      });
      const blob = await response.blob();

      const filenameFromURL = decodeURIComponent(
        src.split('/').pop() || `recording-${Date.now()}`,
      );

      FileSaver.saveAs(blob, filename || filenameFromURL);
    } catch (err) {
      console.error('Failed to download audio file:', err);
    }
  };

  useEffect(() => {
    setIsPlaying(false);
    setTime({ elapsed: 0, duration: 0 });
  }, [src]);

  const isDisabled = disabled || !src;
  const rootClass = classnames(styles.root, className, {
    [styles.rootIsDisabled]: disabled,
  });

  return (
    <div className={rootClass}>
      <audio
        ref={playerRef}
        src={src}
        onDurationChange={onDurationChange}
        onTimeUpdate={onDurationChange}
        onEnded={onAudioEnd}
      />
      <div className={styles.controls}>
        <IconButton
          icon={isPlaying ? 'pause' : 'play'}
          variant="secondary"
          className={styles.button}
          onClick={onPlayToggle}
          disabled={isDisabled}
        />
        <IconButton
          icon="download"
          variant="secondary"
          className={styles.button}
          disabled={!isDownloadable || isDisabled}
          onClick={onSave}
        />
        <div
          className={classnames(styles.progress, {
            [styles.progressIsDisabled]: isDisabled,
          })}
        >
          <span className={styles.duration}>
            {getFormattedDuration(elapsed)}
          </span>
          <div className={styles.slider} onMouseDown={onSliderMouseDown}>
            <Slider
              hideAnnotations
              min={0}
              max={duration}
              value={elapsed}
              onChange={onSeek}
            />
          </div>
          <span className={styles.duration}>
            {getFormattedDuration(duration)}
          </span>
        </div>
        {onDelete && (
          <IconButton
            icon="trash"
            variant="secondary"
            disabled={isDisabled}
            onClick={onDelete}
          />
        )}
      </div>
    </div>
  );
}
